import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import {
  getAdminFeatureCatalog,
  listAdminUsers,
  createAdminUser,
  updateAdminUser,
  deactivateAdminUser,
} from '../api/adminPanelApi';
import { useAuth } from '../contexts/AuthContext';

const BUCKET_LABELS = {
  core_ops: 'Core ops',
  property: 'Property / mapping',
  commercial: 'Commercial',
  finance: 'Finance / payouts',
  automation: 'Site / automation',
  external: 'External',
  meta: 'Meta',
  ivr: 'IVR / calling',
};

/** Extra feature keys if backend catalog is not yet updated (never offer ivr_admin to limited) */
const FALLBACK_IVR_FEATURES = [
  { key: 'ivr_click_to_call', label: 'IVR click-to-call (Booking Details + header dial)' },
];

const emptyCreate = () => ({
  name: '',
  email: '',
  phone: '',
  password: '',
  confirm_password: '',
  features: [],
  hotels: '',
});

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate());

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    const [catalogRes, listRes] = await Promise.all([
      getAdminFeatureCatalog(),
      listAdminUsers(),
    ]);

    if (!catalogRes.success) {
      setError(catalogRes.error || 'Failed to load feature catalog');
      setSnackbar({
        open: true,
        message: catalogRes.error || 'Failed to load catalog',
        severity: 'error',
      });
    } else {
      setGrouped(catalogRes.grouped || {});
    }

    if (!listRes.success) {
      setError(listRes.error || 'Failed to list admin users');
      setSnackbar({
        open: true,
        message: listRes.error || 'Failed to list users',
        severity: 'error',
      });
      setUsers([]);
    } else {
      setUsers(listRes.users || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!me?.is_superadmin) {
      navigate('/', { replace: true });
      return;
    }
    loadAll();
  }, [me, navigate, loadAll]);

  const bucketEntries = useMemo(() => {
    const keys = Object.keys(grouped || {});
    const order = ['core_ops', 'property', 'commercial', 'finance', 'automation', 'external', 'ivr', 'meta'];
    const entries = [
      ...order.filter((k) => grouped[k]),
      ...keys.filter((k) => !order.includes(k)),
    ].map((key) => ({
      key,
      label: BUCKET_LABELS[key] || key,
      items: Array.isArray(grouped[key]) ? grouped[key] : [],
    }));

    const hasIvrC2c = entries.some((b) =>
      (b.items || []).some((item) => {
        const k = typeof item === 'string' ? item : item?.key || item?.feature || item?.id;
        return k === 'ivr_click_to_call';
      })
    );
    // Strip ivr_admin from any catalog bucket — not grantable to limited users
    const cleaned = entries.map((b) => ({
      ...b,
      items: (b.items || []).filter((item) => {
        const k = typeof item === 'string' ? item : item?.key || item?.feature || item?.id;
        return k !== 'ivr_admin';
      }),
    }));
    if (!hasIvrC2c) {
      cleaned.push({
        key: 'ivr',
        label: BUCKET_LABELS.ivr,
        items: FALLBACK_IVR_FEATURES,
      });
    }
    return cleaned.filter((b) => (b.items || []).length > 0);
  }, [grouped]);

  const featureKeyOf = (item) =>
    typeof item === 'string' ? item : item?.key || item?.feature || item?.id || '';

  const featureLabelOf = (item) =>
    typeof item === 'string'
      ? item
      : item?.label || item?.name || item?.title || item?.key || '';

  const toggleFeature = (list, key) =>
    list.includes(key) ? list.filter((f) => f !== key) : [...list, key];

  const parseHotels = (raw) => {
    if (!raw || !String(raw).trim()) return [];
    return String(raw)
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => (Number.isNaN(Number(s)) ? s : Number(s)));
  };

  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.email.trim() || !createForm.phone.trim()) {
      setSnackbar({ open: true, message: 'Name, email and phone are required', severity: 'warning' });
      return;
    }
    if (!createForm.password || createForm.password !== createForm.confirm_password) {
      setSnackbar({ open: true, message: 'Passwords must match', severity: 'warning' });
      return;
    }
    setSaving(true);
    const res = await createAdminUser({
      name: createForm.name,
      email: createForm.email,
      phone: createForm.phone,
      password: createForm.password,
      confirm_password: createForm.confirm_password,
      features: createForm.features,
      hotels: parseHotels(createForm.hotels),
    });
    setSaving(false);
    if (!res.success) {
      setSnackbar({ open: true, message: res.error || 'Create failed', severity: 'error' });
      return;
    }
    setSnackbar({ open: true, message: res.message || 'Admin created', severity: 'success' });
    setCreateOpen(false);
    setCreateForm(emptyCreate());
    loadAll();
  };

  const openEdit = (row) => {
    setEditForm({
      id: row.id,
      name: row.name || '',
      email: row.email || '',
      phone: row.phone || '',
      password: '',
      confirm_password: '',
      features: Array.isArray(row.features) ? [...row.features] : [],
      hotels: Array.isArray(row.hotels) ? row.hotels.join(', ') : '',
      is_active: row.is_active !== false && row.is_active !== 0 && row.is_active !== '0',
      type: row.type,
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editForm?.id) return;
    if (!editForm.phone?.trim()) {
      setSnackbar({ open: true, message: 'Phone is required', severity: 'warning' });
      return;
    }
    if (editForm.password && editForm.password !== editForm.confirm_password) {
      setSnackbar({ open: true, message: 'Passwords must match', severity: 'warning' });
      return;
    }
    setSaving(true);
    const payload = {
      id: editForm.id,
      name: editForm.name,
      phone: editForm.phone,
      features: editForm.features,
      hotels: parseHotels(editForm.hotels),
      is_active: editForm.is_active,
    };
    if (editForm.password) {
      payload.password = editForm.password;
      payload.confirm_password = editForm.confirm_password;
    }
    const res = await updateAdminUser(payload);
    setSaving(false);
    if (!res.success) {
      setSnackbar({ open: true, message: res.error || 'Update failed', severity: 'error' });
      return;
    }
    setSnackbar({
      open: true,
      message: res.message || 'Updated (their sessions may be revoked)',
      severity: 'success',
    });
    setEditOpen(false);
    setEditForm(null);
    loadAll();
  };

  const handleDeactivate = async (row) => {
    if (!window.confirm(`Deactivate ${row.email}? They will be logged out.`)) return;
    setSaving(true);
    const res = await deactivateAdminUser(row.id);
    setSaving(false);
    if (!res.success) {
      setSnackbar({ open: true, message: res.error || 'Deactivate failed', severity: 'error' });
      return;
    }
    setSnackbar({ open: true, message: res.message || 'Deactivated', severity: 'success' });
    loadAll();
  };

  const renderFeatureCheckboxes = (selected, onChange) => (
    <Box sx={{ mt: 1 }}>
      {bucketEntries.map((bucket) => (
        <Accordion key={bucket.key} defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid #e4e8ee', mb: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700} fontSize="0.9rem">
              {bucket.label}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <FormGroup>
              {bucket.items.map((item) => {
                const key = featureKeyOf(item);
                if (!key || key === 'manage_admin_users' || key === 'ivr_admin') return null;
                return (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(key)}
                        onChange={() => onChange(toggleFeature(selected, key))}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {featureLabelOf(item)}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {key}
                        </Typography>
                      </Typography>
                    }
                  />
                );
              })}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      ))}
      {!bucketEntries.length && (
        <Alert severity="warning">No feature catalog loaded. Check getAdminFeatureCatalog.</Alert>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {(loading || saving) && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200 }} />
      )}

      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => navigate('/')}
            size="small"
            sx={{ color: '#FF7700', bgcolor: '#fff', border: '1px solid #e4e8ee' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#2E4057' }}>
              Manage Admin Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Superadmin only · creates limited admins on the admin table (phone + features)
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadAll}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateForm(emptyCreate());
              setCreateOpen(true);
            }}
            sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' }, textTransform: 'none', fontWeight: 700 }}
          >
            Create admin
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ borderRadius: 2.5, overflow: 'hidden', border: '1px solid #e4e8ee' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Email', 'Phone', 'Type', 'Active', 'Features', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f5f7fa' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} sx={{ color: '#FF7700' }} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No admin users returned.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  users.map((row) => {
                    const active =
                      row.is_active !== false && row.is_active !== 0 && row.is_active !== '0';
                    const isSuper = row.type === 'superadmin' || row.is_superadmin;
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.name || '–'}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.phone || '–'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={isSuper ? 'superadmin' : 'admin'}
                            color={isSuper ? 'warning' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={active ? 'yes' : 'no'}
                            color={active ? 'success' : 'default'}
                            variant={active ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(row.features || []).slice(0, 8).map((f) => (
                              <Chip key={f} size="small" label={f} sx={{ height: 22, fontSize: 11 }} />
                            ))}
                            {(row.features || []).length > 8 && (
                              <Chip
                                size="small"
                                label={`+${row.features.length - 8}`}
                                sx={{ height: 22, fontSize: 11 }}
                              />
                            )}
                            {isSuper && (
                              <Chip size="small" label="all" color="warning" sx={{ height: 22, fontSize: 11 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {!isSuper && (
                            <>
                              <Tooltip title="Edit access">
                                <IconButton size="small" onClick={() => openEdit(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {active && (
                                <Tooltip title="Deactivate">
                                  <IconButton size="small" color="error" onClick={() => handleDeactivate(row)}>
                                    <PersonOffIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Create */}
      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create limited admin</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Phone"
                value={createForm.phone}
                onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hotels (optional, comma-separated ids)"
                value={createForm.hotels}
                onChange={(e) => setCreateForm((f) => ({ ...f, hotels: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Confirm password"
                type="password"
                value={createForm.confirm_password}
                onChange={(e) => setCreateForm((f) => ({ ...f, confirm_password: e.target.value }))}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography fontWeight={700} gutterBottom>
            Features
          </Typography>
          {renderFeatureCheckboxes(createForm.features, (features) =>
            setCreateForm((f) => ({ ...f, features }))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving}
            sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' } }}
          >
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit access — {editForm?.email}</DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="New password (optional)"
                    type="password"
                    value={editForm.password}
                    onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm new password"
                    type="password"
                    value={editForm.confirm_password}
                    onChange={(e) => setEditForm((f) => ({ ...f, confirm_password: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hotels (optional)"
                    value={editForm.hotels}
                    onChange={(e) => setEditForm((f) => ({ ...f, hotels: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!editForm.is_active}
                        onChange={(e) => setEditForm((f) => ({ ...f, is_active: e.target.checked }))}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                Changing features, password, or active status revokes their sessions — they must re-login.
              </Alert>
              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={700} gutterBottom>
                Features
              </Typography>
              {renderFeatureCheckboxes(editForm.features, (features) =>
                setEditForm((f) => ({ ...f, features }))
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={saving}
            sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;
